import * as React from 'react';
import { useDispatch } from 'react-redux';
import * as _ from 'lodash';
import {
  API,
  Logging,
  UserInfo,
  PermissionGroup,
  UserContext,
  getCreatePermission,
  getEditPermission,
  ConfirmationModal,
  NotificationData,
  Notification,
  ResponseError,
  getUuidV4,
  SellerCollection,
} from '@vcd-ecm-ui/Common';
import {
  GenericGrid,
  ActionMenu,
  ColumnLink,
  FilterModelApi,
  GridDataSummaryCollection,
  SortModelApi,
  Action,
  ActionDivider,
  mergeDefaultAndCustomColumns,
} from '@vcd-ecm-ui/GenericGrid';
import { VtxButton, VtxColumnProps, VtxIcon } from '@vertexinc/vtx-ui-react-component-library';
import SellersProps from './SellersProps.interface';
import GridRowData, { GridDataNativeType } from './GridRowData';
import getDefaultColumns from './defaultColumns';
import '../common-ecwizard.scss';
import './seller.scss';

const Sellers = ({
  gatewayEndPoint,
}: SellersProps): JSX.Element => {
  // Get the User Context
  const userContext: UserInfo = React.useContext(UserContext);
  const permissionECWizardCreate: boolean = getCreatePermission(userContext, PermissionGroup.ECWizard);
  const permissionECWizardEdit: boolean = getEditPermission(userContext, PermissionGroup.ECWizard);

  const [isConfirmationOpen, setIsConfirmationOpen] = React.useState(false);
  const [deleteSeller, setDeleteSeller] = React.useState({ name: null, uuid: null });
  const [notification, setNotification] = React.useState(null);
  const [manualRefreshKey, setManualRefreshKey] = React.useState(null);

  const dispatch = useDispatch();

  // Delete link was clicked
  const onSellerDelete = (uuid: string, name: string): void => {
    Logging.Info('Delete Seller', uuid, name);

    // Save the Seller Name and Id
    setDeleteSeller({ name, uuid });
    setIsConfirmationOpen(true);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderAction = (value: any, row: GridRowData, index: number): JSX.Element => {
    Logging.Debug('Render Action', value, row, index);

    return (
      <>
        {
          permissionECWizardEdit
          && (
            <div className="ecwizardActions">
              <ColumnLink
                id="deleteLink"
                linkText="Delete"
                onClick={(event) => onSellerDelete(row.rowData.uuid, row.rowData.name)}
                align="center"
                row={row}
              />
              |
              <ColumnLink
                id="editLink"
                linkText="Edit"
                linkAddress={`#/ecwizard/seller/edit/${row.rowData.uuid}`}
                align="center"
                row={row}
              />
            </div>
          )
        }
      </>
    );
  };

  // Parse each row from the Native Data Type to the Actual Data Type
  const rowParser = (row: GridDataNativeType): GridRowData => GridRowData.fromData(row);

  // Get the Columns for the Grid
  const getColumns = (): VtxColumnProps<GridRowData>[] => {
    // Get the Default Columns
    const defaultColumns: VtxColumnProps<GridRowData>[] = getDefaultColumns();

    // Column Columns with Dynamic Renderer
    const customColumns = [
      {
        dataIndex: 'action',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (value: any, row: GridRowData, index: number) => renderAction(value, row, index),
      },
    ];

    // Merge the Custom Columns into the Default Columns
    return mergeDefaultAndCustomColumns(defaultColumns, customColumns);
  };

  // The API Call used to retrieve data for the Grid
  const apiGetRowsServerModel = (
    startRow: number,
    endRow: number,
    filterModel: FilterModelApi,
    sortModel: SortModelApi,
  ): Promise<GridDataSummaryCollection> => {
    const api = new API(gatewayEndPoint);

    return api.ecwizard.getSellersV41(startRow, endRow, filterModel, sortModel);
  };

  // Make the API Call to Delete the Seller
  const deleteSellerAction = (uuid: string, name: string) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Delete the Seller
      await api.ecwizard.deleteSellerV41(uuid);

      // Reload the Grid
      setManualRefreshKey(getUuidV4());
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Seller Delete Error',
        description: `Unable to Delete the Seller '${name}'.`,
        error: error as ResponseError,
      };

      setNotification(notificationData);
    }
  };

  // Delete Confirmation : OK
  const onOKConfirmation = (): void => {
    Logging.Info('OK Confirmation');
    setDeleteSeller({ name: null, uuid: null });
    setIsConfirmationOpen(false);

    // Delete the Seller
    dispatch(deleteSellerAction(deleteSeller.uuid, deleteSeller.name));
  };

  // Delete Confirmation : Cancel
  const onCancelConfirmation = (): void => {
    Logging.Info('Cancel Confirmation');
    setDeleteSeller({ name: null, uuid: null });
    setIsConfirmationOpen(false);
  };

  // Notification Error was Closed
  const onCloseNotification = (): void => {
    setNotification(null);
  };

  React.useEffect(() => {
    // This Code will run once after the initial Render
    Logging.Debug('Sellers: Mount');

    return (): void => {
      // This Code will run once the component unmounts.
    };
  }, []);

  const confirmationMessage = `Removing the '${deleteSeller?.name}' seller cannot be undone.`;

  return (
    <>
      <div className="ecwizardContainer">
        <div className="ecwizardHeader">
          <div className="ecwizardHeaderLeft">
            Sellers
          </div>
          <div className="ecwizardHeaderRight">
            {permissionECWizardCreate && (
              <a href="#/ecwizard/seller/add" title="Add Seller">
                <VtxButton
                  id="newButton"
                  icon={<VtxIcon type="plus" />}
                  type="primary"
                >
                  Add Seller
                </VtxButton>
              </a>
            )}
          </div>
        </div>
        <div className="ecwizardHeaderDescription">
          Below are all sellers that you currently have configured to enable buyer tax exemption.
        </div>
        <div className="ecwizardContentGrid">
          <GenericGrid<GridRowData>
            id="sellerGrid"
            dataTypeDescription="Sellers"
            getDefaultColumns={getColumns}
            rowParser={rowParser}
            apiGetRowsServerModel={apiGetRowsServerModel}
            verticalHeightOffset={366}
            model="server"
            autoRefreshSeconds={60}
            useCache={false} // Cannot use cache if Auto Refresh
            refreshKey={manualRefreshKey}
          />
        </div>
        <ConfirmationModal
          isOpen={isConfirmationOpen}
          heading="Are you sure you want to continue?"
          message={confirmationMessage}
          okButtonName="Delete"
          onOK={onOKConfirmation}
          cancelButtonName="Cancel"
          onCancel={onCancelConfirmation}
          confirmationType="Error"
        />
        <Notification
          data={notification}
          onNotificationClose={onCloseNotification}
          autoClose
        />
      </div>
    </>
  );
};

export default Sellers;
